import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/press-release-generator';

const PressReleaseLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Press Release Generator | Explanatum';
    const desc = 'Draft AP-style press releases in minutes. Generate headlines, quotes, boilerplates, and media-ready copy without hiring agencies or Fiverr freelancers.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What formats does the generator support?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'We produce AP-style press releases with headlines, datelines, body copy, boilerplates, and media contact information ready for distribution.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I include quotes and company boilerplates?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Yes. Provide quotes, titles, and boilerplate details once. Explanatum formats them consistently across every release.',
        },
      },
      {
        '@type': 'Question',
        name: 'How fast are drafts delivered?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Most users get a polished draft in under a minute, compared with days waiting on agencies or freelancers.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I collaborate with my team?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Share drafts inside Explanatum, gather edits, and send approved versions straight to email campaigns or newsroom pages.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does it integrate with other Explanatum modules?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Yes. Hand off releases to the AI Business Consultant, CRM, and marketing automation for coordinated follow-up.',
        },
      },
    ],
  } as const;

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Press release generator overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/40 bg-blue-600/10 px-4 py-1 text-sm font-semibold text-purple-200">
                <span className="inline-flex h-2 w-2 rounded-full bg-blue-400" />
                AI Press Release Generator
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Ship newsroom-ready releases without agencies.
                <span className="mt-2 block text-blue-600">
                  Headlines, quotes, boilerplates—done in minutes.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Brief Explanatum once and get polished, AP-style press releases ready for media outreach, investor updates, and website newsrooms. Stop waiting on consultants or juggling Fiverr revisions.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_press_release_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-blue-600 via-indigo-500 to-sky-400 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-blue-600/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/marketing/press-release-generator"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Generate a release now
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  AP-style formatting every time
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  Quotes, boilerplates, and contact info included
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Sync with consultant and email campaigns
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Preview of generated release">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-600/30 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Headline</span>
                  <span>Release preview</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">“Explanatum Launches AI Media Suite to Automate PR, Press Releases, and Analyst Briefings.”</p>
                    <p className="mt-3 text-[12px] text-gray-500">Dateline, executive quote, and key facts laid out exactly how newsrooms expect.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Boilerplate, contact details, and CTA ready to distribute to media lists or upload to your newsroom.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Media-ready releases with marketing follow-through</h2>
            <p className="mt-4 text-base text-gray-500">Keep every announcement on-message, consistent, and synced with your wider go-to-market playbook.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Consistent AP structure',
              body: 'Every release includes headline, dateline, summary paragraph, quotes, boilerplate, and media contacts.'
            }, {
              title: 'Reusable quote library',
              body: 'Store executive and customer quotes once, then refresh them on the fly for new announcements.'
            }, {
              title: 'Instant distribution-ready copy',
              body: 'Download clean text or push to email, newsroom pages, and social campaigns in one click.'
            }, {
              title: 'Consultant coordination',
              body: 'Ask the AI Business Consultant to craft follow-up outreach and analyst briefings from the same source of truth.'
            }, {
              title: 'Investor relations support',
              body: 'Include metrics, funding milestones, and compliance-friendly statements without risking messaging drift.'
            }, {
              title: 'Localization & variations',
              body: 'Clone releases for different regions, products, or partner announcements in minutes.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why teams replace agencies and Fiverr gigs</h2>
            <p className="mt-4 text-base text-gray-500">Spend less time chasing drafts and more time landing coverage.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum AI Press Release Generator',
              points: [
                'Unlimited releases included with your subscription',
                'Drafts ready in under a minute with consistent AP formatting',
                'Quotes, boilerplates, and media contacts stored centrally',
                'Hand off to consultants, CRM, and outreach automations',
                'Easy to duplicate for product launches, funding, or partnerships'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Human PR consultants',
              points: [
                '£500–£2,000 per release with multi-day timelines',
                'Requires interviews, revisions, and scheduled calls',
                'Limited iterations—extra drafts cost extra',
                'Delivered via documents with no automation or integrations',
                'Difficult to scale for frequent announcements'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Fiverr freelancers',
              points: [
                'Low upfront cost (£40–£150) but quality varies widely',
                'Turnaround depends on availability—often 3–5 days',
                'Multiple revisions or versions incur add-on fees',
                'No guarantee of AP style or aligned messaging',
                'Assets live in email threads with no connection to your stack'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-gray-200 bg-sky-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Need a press release ready before tomorrow’s launch?”</h3>
            <p className="mt-3 text-base text-gray-600">Generate the announcement, share with stakeholders, and push it into outreach cadences without leaving Explanatum.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_press_release_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-blue-600 via-indigo-500 to-sky-400 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-blue-500/30 hover:scale-[1.02]"
              >
                Run a press release sprint
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with the Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where Explanatum accelerates communications</h2>
            <p className="mt-4 text-base text-gray-500">From launches to fundraising, keep every stakeholder informed with minimal lift.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Product launches',
              body: 'Ship embargoed releases, investor summaries, and marketing briefs in one workflow.'
            }, {
              title: 'Funding announcements',
              body: 'Highlight milestones, partner quotes, and metrics without breaching compliance.'
            }, {
              title: 'Partnership or customer wins',
              body: 'Document mutual value, craft regional variants, and notify account teams automatically.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section aria-label="FAQs" className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-gray-900">Frequently asked questions</h2>
          <div className="mt-6 space-y-4">
            {[{
              q: 'How do I make sure the release fits my brand voice?',
              a: 'Provide tone preferences or sample content. The AI mirrors your voice and keeps terminology consistent across releases.'
            }, {
              q: 'Can I export to specific newsroom templates?',
              a: 'Yes—copy and paste from the formatted output or export text to drop into your CMS or media distribution tool.'
            }, {
              q: 'Does it handle multimedia embeds?',
              a: 'The generator includes callouts and links. You can add multimedia assets in your CMS while keeping messaging aligned.'
            }, {
              q: 'Will we still have access to human review?',
              a: 'Absolutely. Use Explanatum to draft, then invite comms leads or legal reviewers to edit before publishing.'
            }].map((item) => (
              <details key={item.q} className="group rounded-2xl border border-gray-200 bg-white p-5 shadow">
                <summary className="cursor-pointer text-lg font-semibold text-gray-900 group-open:text-sky-700">{item.q}</summary>
                <p className="mt-3 text-sm text-gray-500">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default PressReleaseLanding;
